import { createFileRoute } from '@tanstack/react-router';
import { Button, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  createBrandDocument,
  myBrandsDocument,
} from '@social-garden/api/documents/brand.ts';
import { useNavigate } from '@tanstack/react-router';
import ImageCropField from '@/components/ImageCropField.tsx';
import {
  CreateBrandFieldValues,
  CreateBrandSchema,
} from '@/constants/ValidationSchema.ts';
import { FormFieldProps } from '@/utils/types.ts';
import BrandNameField from '@/components/BrandNameField.tsx';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';

export const Route = createFileRoute('/_manager/_setup/create-first-brand')({
  component: CreateFirstBrand,
});

function CreateFirstBrand() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'manager']);
  const [createBrand, { loading }] = useMutation(createBrandDocument, {
    refetchQueries: [myBrandsDocument],
  });

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isValid, isSubmitting: isFormSubmitting },
  } = useForm<CreateBrandFieldValues>({
    mode: 'all',
    resolver: zodResolver(CreateBrandSchema),
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting: isFormSubmitting });

  const renderLogoField = useCallback(
    ({
      field: { name, onChange },
      fieldState: { error, invalid },
      formState: { isSubmitting },
    }: FormFieldProps<CreateBrandFieldValues, 'logo'>) => (
      <ImageCropField
        fullWidth
        label={t('common:brand.logo')}
        width={500}
        height={500}
        disabled={isSubmitting}
        error={invalid}
        helperText={error?.message}
        name={name}
        cropShape="round"
        onChange={onChange}
      />
    ),
    [t],
  );

  const handleOnValid = useCallback(
    async ({ logo, name }: CreateBrandFieldValues) => {
      const { data } = await createBrand({
        variables: {
          input: {
            logo,
            name,
          },
        },
      });
      if (data?.createBrand) {
        await navigate({ to: '/' });
      }
    },
    [createBrand, navigate],
  );

  return (
    <Container
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 6,
      }}
      maxWidth="md"
      component="form"
      noValidate
      onSubmit={handleSubmit(handleOnValid)}>
      <Typography variant="h2" align="center">
        {t('manager:noBrand.title')}
      </Typography>
      <Typography variant="h5" align="center">
        {t('manager:noBrand.message')}
      </Typography>
      <Stack spacing={2}>
        <Controller<CreateBrandFieldValues, 'logo'>
          control={control}
          render={renderLogoField}
          name="logo"
        />
        <Controller<CreateBrandFieldValues, 'name'>
          control={control}
          render={BrandNameField}
          name="name"
        />
        <Button
          type="submit"
          variant="contained"
          loading={loading}
          disabled={!isValid}>
          {t('manager:brand.create.submitButton')}
        </Button>
      </Stack>
    </Container>
  );
}
